import { SettingsOutlined } from '@mui/icons-material';
import { Alert, Box, Button, Stack } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';
import { useCallback, useMemo, useState } from 'react';
import type { ApiError } from '../../@types';
import type { GoldGamesFreeSpinQuery, GoldGamesFreeSpinRow } from '../../@types/goldGames';
import { useModal } from '../../contexts/ModalContext';
import { useCasinoTableQuery } from '../../hooks/useCasinoTableQuery';
import { useToastErrorMessage } from '../../hooks/useToastErrorMessage';
import { useCancelGoldGamesCampaign, useGoldGamesFreeSpins, useParticipateGoldGames } from '../../queries/goldGames';
import { createColumn } from '../../utils';
import ConfirmationModal from '../modals/ConfirmationModal';
import FormModal from '../modals/FormModal';
import DateRangeSelect, { type DayjsTimestamp } from '../molecules/DateRangeSelect';
import TableTemplate from '../templates/TableTemplate';
import GoldGamesCampaignDetails from './GoldGamesCampaignDetails';

type Props = { marketplaceId: string; currency: string };
const canCancelCampaign = (row: GoldGamesFreeSpinRow) => !!row.campaignId && row.campaignStatus === 'active';
const canRetryAward = (row: GoldGamesFreeSpinRow) =>
  canCancelCampaign(row) && ['pending', 'unknown'].includes(row.status);

const GoldGamesFreeSpinsTable = ({ marketplaceId, currency }: Props) => {
  const [date, setDate] = useState<DayjsTimestamp>(() => ({
    fromTimestamp: dayjs().startOf('month'),
    toTimestamp: dayjs().endOf('day'),
  }));
  const { queryParams, paginationModel, updateQueryParams, changeQuery, handleSearch, changeDateRange } =
    useCasinoTableQuery<GoldGamesFreeSpinQuery>({
      defaultQuery: { page: 1, limit: 25, dateFrom: date.fromTimestamp.valueOf(), dateTo: date.toTimestamp.valueOf() },
      searchField: 'search',
      emptyFields: ['search', 'playerId', 'campaign'],
    });
  const history = useGoldGamesFreeSpins(marketplaceId, queryParams);
  const cancel = useCancelGoldGamesCampaign();
  const participate = useParticipateGoldGames();
  const { openModal } = useModal();
  const { handleErrorMessage } = useToastErrorMessage();
  const { mutateAsync: cancelCampaign, isPending: isCancelling } = cancel;
  const { mutateAsync: participatePlayers, isPending: isParticipating } = participate;
  const actionsPending = isCancelling || isParticipating;

  const viewCampaign = useCallback(
    (row: GoldGamesFreeSpinRow) => {
      openModal(<GoldGamesCampaignDetails marketplaceId={marketplaceId} recordId={row.recordId} />, undefined, {
        dialogSize: 'wide',
      });
    },
    [marketplaceId, openModal]
  );

  const retryAward = useCallback(
    (row: GoldGamesFreeSpinRow) => {
      if (!canRetryAward(row) || !row.campaignId) return;
      const campaignId = row.campaignId;
      const confirmAward = async () => {
        await participatePlayers({ campaignId, playerIds: [row.playerId] });
      };
      openModal(
        <ConfirmationModal
          title={`Award free spins to ${row.player} in ${row.campaign}?`}
          actionButtonLabel='Award'
          actionButtonClose='Back'
          handleConfirm={confirmAward}
        />
      );
    },
    [openModal, participatePlayers]
  );

  const cancelAwardCampaign = useCallback(
    (row: GoldGamesFreeSpinRow) => {
      if (!canCancelCampaign(row) || !row.campaignId) return;
      const campaignId = row.campaignId;
      const confirmCancel = async () => {
        try {
          await cancelCampaign(campaignId);
        } catch (error) {
          handleErrorMessage(error as ApiError);
          throw error;
        }
      };
      openModal(
        <ConfirmationModal
          title={`Cancel campaign ${row.campaign} for all its players?`}
          actionButtonLabel='Cancel campaign'
          actionButtonClose='Back'
          handleConfirm={confirmCancel}
        />
      );
    },
    [cancelCampaign, handleErrorMessage, openModal]
  );
  const columns = useMemo<GridColDef<GoldGamesFreeSpinRow>[]>(
    () => [
      createColumn('id', 'ID', { minWidth: 200 }),
      createColumn('game', 'Game', { minWidth: 180 }),
      createColumn('player', 'Player', { minWidth: 150 }),
      createColumn('campaign', 'Campaign', { minWidth: 180 }),
      createColumn('quantity', 'Quantity', { minWidth: 110, type: 'number' }),
      createColumn('amount', 'Amount', {
        minWidth: 120,
        valueGetter: (_value, row) => (row.amount == null ? 'Not recorded' : `${row.amount} ${row.currency}`),
      }),
      createColumn('dateAdded', 'Date Added', {
        minWidth: 180,
        valueGetter: (_value, row) => dayjs(row.dateAdded).format('DD/MM/YYYY HH:mm:ss'),
      }),
      createColumn('addedBy', 'Added By', { minWidth: 140 }),
      createColumn('status', 'Status', { minWidth: 110 }),
      createColumn('options', 'Options', {
        minWidth: 340,
        sortable: false,
        disableExport: true,
        renderCell: ({ row }) => (
          <Stack direction='row' spacing={1}>
            <Button onClick={() => viewCampaign(row)}>View campaign</Button>
            {canRetryAward(row) && (
              <Button disabled={actionsPending} onClick={() => retryAward(row)}>
                Retry award
              </Button>
            )}
            {canCancelCampaign(row) && (
              <Button disabled={actionsPending} onClick={() => cancelAwardCampaign(row)}>
                Cancel campaign
              </Button>
            )}
          </Stack>
        ),
      }),
    ],
    [actionsPending, cancelAwardCampaign, retryAward, viewCampaign]
  );

  return (
    <Box sx={{ height: 'calc(100% - 200px)' }}>
      <Alert severity='info' sx={{ mb: 2 }}>
        Saved locally, one row per player. Awarded means Gold Games confirmed participation, not that spins were played.
        Unknown campaigns or stuck awarding records require support reconciliation; do not recreate them.
      </Alert>
      {history.isError && (
        <Alert severity='error' action={<Button onClick={() => history.refetch()}>Retry</Button>}>
          Unable to load Gold Games free spins.
        </Alert>
      )}
      {participate.data && (
        <Alert severity={participate.data.failedPlayerIds.length ? 'warning' : 'success'}>
          Campaign {participate.data.campaignId}: {participate.data.awarded} players confirmed;{' '}
          {participate.data.failedPlayerIds.length} could not be confirmed.
        </Alert>
      )}
      <TableTemplate
        rows={history.data?.items ?? []}
        columns={columns}
        rowCount={history.data?.count ?? 0}
        loading={history.isFetching}
        paginationModel={paginationModel}
        handlePaginationModelChange={updateQueryParams}
        handleSearch={handleSearch}
        changeQuery={changeQuery}
        showFilter
        filterType='gold-games-free-spins'
        pageSizeOptions={[25, 50, 100, 200, 500]}
        customTableHeaderContent={
          <Stack
            role='group'
            aria-label='Free spins controls'
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            useFlexGap
            sx={{
              width: '100%',
              flexWrap: 'wrap',
              justifyContent: 'flex-end',
              alignItems: { xs: 'stretch', md: 'center' },
            }}
          >
            <DateRangeSelect
              date={date}
              defaultDateRangeOption='custom'
              onDateChange={(value) => {
                setDate(value);
                changeDateRange({ dateFrom: value.fromTimestamp.valueOf(), dateTo: value.toTimestamp.valueOf() });
              }}
            />
            <Button
              variant='outlined'
              startIcon={<SettingsOutlined />}
              disabled={!currency}
              onClick={() =>
                openModal(
                  <FormModal form='gold-games-free-spins' marketplaceId={marketplaceId} currency={currency} />,
                  undefined,
                  { dialogSize: 'wide' }
                )
              }
            >
              Free Spins Campaigns
            </Button>
          </Stack>
        }
      />
    </Box>
  );
};
export default GoldGamesFreeSpinsTable;
